import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import EmptyView from 'components/Shared/EmptyView'
import { useMediaQuery } from 'hooks'
import { useItcStatus, useItcStatusDispatch } from 'components/Recommendations/Itc/ItcStatusContextProvider'
import { actions } from 'components/Recommendations/Itc/itc'
import { SecondaryButton } from 'components/Shared/Button'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import Notice from 'components/Shared/Notice'
import EventDialog from 'components/Recommendations/Itc/EventDialog'

export default function NoContentMessage({ eventId }) {
  const { t } = useTranslation()
  const {
    success: itcStatusSuccess,
    mappedStatus: itcMappedStatus,
    status: itcStatus,
    loading: itcStatusLoading,
  } = useItcStatus()
  const dispatch = useItcStatusDispatch()
  const history = useHistory()
  const isSmallScreen = useMediaQuery('(max-width: 992px)')
  const [itcModalOpen, setItcModalOpen] = useState(false)
  const setEditSearchParam = (enabled) => {
    const searchParams = new URLSearchParams(history.location.search)
    if (enabled) {
      searchParams.set('edit', 'true')
    } else {
      searchParams.delete('edit')
    }

    const nextSearch = searchParams.toString()
    history.replace({
      ...history.location,
      search: nextSearch ? `?${nextSearch}` : '',
    })
  }

  useEffect(() => {
    if (!(itcStatusSuccess && itcMappedStatus === 'running')) {
      return undefined
    }

    const intervalId = setInterval(() => {
      dispatch({ type: actions.fetchStatusAsync, payload: { eventId, silent: true } })
    }, 30000)

    return () => clearInterval(intervalId)
  }, [dispatch, eventId, itcMappedStatus, itcStatusSuccess])

  if (itcStatusLoading) {
    return <LoadingSpinner className="m-3" title={t('common.loading')} />
  }

  if (itcStatusSuccess && itcMappedStatus === 'running') {
    return (
      <Notice 
        message={t('Recommendations.OpenCampaigns.itc.states.running')} 
        isVisible={true} 
        isWarning={false} 
      />
    )
  }

  if (itcStatusSuccess && itcMappedStatus === 'content_error') {
    return  ( 
      <>
        <div className="open-campaigns_itc-status m-3">
          <p className="open-campaigns_itc-status__message failed">
            {t(itcStatus.reason ? `Recommendations.OpenCampaigns.itc.errorMessage.content.${itcStatus.reason}` : 'Recommendations.OpenCampaigns.itc.errorMessage.content.other')}
          </p>
    
          <SecondaryButton
            text={t('Recommendations.OpenCampaigns.itc.states.action-edit')}
            onClick={() => {
              if (isSmallScreen) {
                history.push(`/campaigns/${eventId}/itc?edit=true`)
              } else {
                setEditSearchParam(true)
                setItcModalOpen(true)
              }
            }}
            color="navy"
            disabled={itcStatusLoading}
            fullWidth={false}
          />
        </div>
        
        <EventDialog
          eventId={eventId}
          isOpen={itcModalOpen}
          onClose={(success) => {
            setItcModalOpen(false)
            setEditSearchParam(false)
            if (success) {
              dispatch({ type: actions.fetchStatusAsync, payload: { eventId } })
            }
          }}
        />
      </>
    )}

  if (itcStatusSuccess && itcMappedStatus === 'failed') {
    return  ( 
      <div className="open-campaigns_itc-status m-3">
        <p className="open-campaigns_itc-status__message failed">
          {t('Recommendations.OpenCampaigns.itc.errorMessage.noContent')}
        </p>
    
        <SecondaryButton
          text={t('Recommendations.OpenCampaigns.itc.states.action-failed')}
          onClick={() => {
            dispatch({ type: actions.regenerateItcAsync, payload: { eventId } })
          }}
          color="navy"
          disabled={itcStatusLoading}
          fullWidth={false}
        />
      </div>
    )}

  return <EmptyView>{t('Recommendations.emptyCampaigns', { status: '' })}</EmptyView>
}

NoContentMessage.propTypes = {
  eventId: PropTypes.string.isRequired,
}